// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: LocationDetailsPrimaryImageStyles;
    badgePostion?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface LocationDetailsPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;
}

export const locationDetailsPrimaryImageStyles: LocationDetailsPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 150px;
            flex-grow: 1;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    hiddenWrapper: {
        css: css`
            opacity: 0;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    image: {
        css: css`
            cursor: pointer;
            width: 100%;
        `,
    },
};

const LocationDetailsPrimaryImage = ({ image, useLargeImage, onClick, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeToNew(locationDetailsPrimaryImageStyles, extendedStyles));

    const [lastHeight, setLastHeight] = useState(0);
    const [lastInstance, setLastInstance] = useState<HTMLElement | null>(null);
    const setRefHandler = (instance: HTMLElement | null) => {
        if (instance) {
            setLastInstance(instance);
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const onLoadHandler = useCallback(() => {
        setIsLoading(false);
        setTimeout(() => {
            if (lastInstance && lastInstance.clientHeight > 0) {
                setLastHeight(lastInstance.clientHeight);
            }
        }, 100);
    }, [setIsLoading, lastInstance, setLastHeight]);

    useEffect(() => {
        if (image?.imageType === "Static") {
            setIsLoading(true);
        }
    }, [image?.id]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            // onClick?.();
        }
    };

    return (
        <LoadingOverlay
            loading={isLoading}
            css={css`
                height: ${lastHeight}px;
                ${isLoading ? `max-height: 366px;` : ""}
            `}
        >
            {image.imageType === "Static" && (
                <StyledWrapper
                    css={css`
                        position: relative;
                        flex-grow: 1;
                        display: flex;
                        justfiy-content: center;
                        align-items: center;
                        width: 100% !important;
                    `}
                    ref={setRefHandler}
                    onClick={imageWrapperClickHandler}
                >
                    <LazyImage
                        {...styles.image}
                        key={image.id}
                        src={path}
                        cloudinaryWidth={styles?.image?.cloudinaryWidth}
                        cloudinary={styles?.image?.cloudinary}
                        altText={image.imageAltText}
                        onLoad={onLoadHandler}
                        onError={onLoadHandler}
                        data-test-selector="productDetails_mainImage"
                        onKeyPress={imageWrapperKeyHandler}
                        tabIndex={0}
                    />
                </StyledWrapper>
            )}
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps)(LocationDetailsPrimaryImage);
